import { useCallback, useEffect, useState } from 'react';
import type { ApiClient, AuditLog, Plan, UserDetail as UserDetailPayload } from '../api';
import { formatDate, formatMoney, paymentTag, planTag, promptForReason, renderError, statusTag } from './shared';

interface Props {
  client: ApiClient;
  userId: string;
  onBack: () => void;
}

export function UserDetail({ client, userId, onBack }: Props) {
  const [detail, setDetail] = useState<UserDetailPayload | null>(null);
  const [audit, setAudit] = useState<AuditLog[] | null>(null);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // A monotonic counter that mutating actions bump to force a reload. Keeps
  // the fetch effect keyed on primitive values only, so React re-runs it when
  // we intend to reload and never because of a fresh callback identity.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setDetail(null);
    setAudit(null);
    Promise.all([client.getUser(userId), client.getAudit(userId), client.getPlans()])
      .then(([d, a, p]) => {
        if (cancelled) return;
        setDetail(d);
        setAudit(a.logs);
        setPlans(p.plans);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [client, userId, reloadTick]);

  const run = useCallback(async (task: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await task();
      setReloadTick((tick) => tick + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }, []);

  if (error !== null && detail === null) {
    return (
      <>
        <button className="link" onClick={onBack}>
          ← Back
        </button>
        {renderError(error)}
      </>
    );
  }
  if (!detail || !audit || !plans) {
    return <p className="muted">Loading…</p>;
  }

  const { user, subscription, entitlements, grants, devices, identities } = detail;
  const activeGrants = grants.filter((grant) => !grant.revokedAt);
  const activeSubscription = subscription.plan !== 'free' || subscription.status !== 'none';

  return (
    <>
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <button className="link" onClick={onBack}>
          ← Back to users
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {user.status === 'active' && (
            <button
              className="danger"
              disabled={busy}
              onClick={async () => {
                const reason = await promptForReason('suspending this account');
                if (!reason) return;
                void run(() => client.suspend(user.id, reason));
              }}
            >
              Suspend
            </button>
          )}
          {user.status === 'suspended' && (
            <button
              className="primary"
              disabled={busy}
              onClick={async () => {
                const reason = await promptForReason('reinstating this account');
                if (!reason) return;
                void run(() => client.reinstate(user.id, reason));
              }}
            >
              Reinstate
            </button>
          )}
          {user.status !== 'deleted' && (
            <button
              className="danger"
              disabled={busy}
              onClick={async () => {
                if (!window.confirm('Anonymize and delete this account? Billing/audit rows are kept.')) return;
                const reason = await promptForReason('deletion (GDPR)');
                if (!reason) return;
                await run(() => client.deleteUser(user.id, reason));
                onBack();
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {error !== null && renderError(error)}

      <div className="panel">
        <h2>Account</h2>
        <dl className="row">
          <dt>Internal id</dt>
          <dd>
            <kbd>{user.id}</kbd>
          </dd>
          <dt>Logto id</dt>
          <dd>
            <kbd>{user.logtoUserId}</kbd>
          </dd>
          <dt>Email</dt>
          <dd>{user.email ?? user.maskedEmail ?? <span className="muted">—</span>}</dd>
          <dt>Display name</dt>
          <dd>{user.displayName ?? <span className="muted">—</span>}</dd>
          <dt>Status</dt>
          <dd>
            {statusTag(user.status)}{' '}
            {user.statusReason && <span className="muted">— {user.statusReason}</span>}
          </dd>
          <dt>Created</dt>
          <dd>{formatDate(user.createdAt)}</dd>
          <dt>Last login</dt>
          <dd>{formatDate(user.lastLoginAt)}</dd>
          {user.deletedAt && (
            <>
              <dt>Deleted</dt>
              <dd>{formatDate(user.deletedAt)}</dd>
            </>
          )}
        </dl>
      </div>

      <div className="grid-two">
        <SubscriptionCard
          subscription={subscription}
          active={activeSubscription}
          plans={plans}
          busy={busy}
          onStart={(planKey) =>
            promptForReason(`starting a ${planKey} subscription`).then((reason) => {
              if (!reason) return;
              void run(() => client.startSubscription(user.id, { planKey, provider: 'mock', reason }));
            })
          }
          onCancel={() => {
            // Latest subscription id isn't in the summary — the API caller fetches it fresh.
            void (async () => {
              const reason = await promptForReason('cancelling this subscription');
              if (!reason) return;
              // The user detail doesn't expose subscription ids yet; use the newest
              // audit row that names one, or fall back to a full refresh.
              const started = audit.find(
                (log) => log.action === 'subscription.started' && log.metadata?.subscriptionId,
              );
              const id = (started?.metadata as { subscriptionId?: string })?.subscriptionId;
              if (!id) {
                setError({ message: 'No subscription id found in the audit log' });
                return;
              }
              void run(() => client.cancelSubscription(id, { atPeriodEnd: false, reason }));
            })();
          }}
        />
        <EntitlementsCard
          entitlements={entitlements}
          grants={activeGrants}
          busy={busy}
          onGrant={(payload) => run(() => client.grantEntitlement(user.id, payload))}
          onRevoke={(id) =>
            promptForReason('revoking this grant').then((reason) => {
              if (!reason) return;
              void run(() => client.revokeEntitlement(id, reason));
            })
          }
        />
      </div>

      <div className="panel">
        <h2>Devices ({devices.length})</h2>
        {devices.length === 0 ? (
          <p className="muted">No registered devices.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Platform</th>
                <th>App version</th>
                <th>Last seen</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.name}</td>
                  <td>{device.platform}</td>
                  <td>{device.appVersion ?? '—'}</td>
                  <td>{formatDate(device.lastSeenAt)}</td>
                  <td>
                    {device.revokedAt ? statusTag('revoked') : statusTag('active')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h2>Payments ({detail.payments.length})</h2>
        {detail.payments.length === 0 ? (
          <p className="muted">No payments.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th>What</th>
                <th>Amount</th>
                <th>Status</th>
                <th>PayU order</th>
                <th>Covers until</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.createdAt)}</td>
                  <td>{payment.description}</td>
                  <td>{formatMoney(payment.amountMinor, payment.currency)}</td>
                  <td title={payment.providerStatus ?? undefined}>{paymentTag(payment.status, payment.providerStatus)}</td>
                  <td>
                    <code>{payment.providerOrderId ?? '—'}</code>
                  </td>
                  <td>{formatDate(payment.periodEnd)}</td>
                  <td>
                    {payment.status === 'pending' && (
                      <button
                        disabled={busy}
                        onClick={async () => {
                          const reason = await promptForReason('re-checking this payment with PayU');
                          if (!reason) return;
                          void run(() => client.recheckPayment(payment.id, reason));
                        }}
                      >
                        Re-check with PayU
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h2>Identities ({identities.length})</h2>
        {identities.length === 0 ? (
          <p className="muted">No linked sign-in methods.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Email</th>
                <th>Linked at</th>
              </tr>
            </thead>
            <tbody>
              {identities.map((identity, index) => (
                <tr key={`${identity.provider}-${index}`}>
                  <td>{identity.provider}</td>
                  <td>{identity.email ?? '—'}</td>
                  <td>{formatDate(identity.linkedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h2>Audit trail</h2>
        {audit.length === 0 ? (
          <p className="muted">Nothing logged yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((log) => (
                <tr key={log.id}>
                  <td>{formatDate(log.createdAt)}</td>
                  <td>{log.actorType}</td>
                  <td>
                    <code>{log.action}</code>
                  </td>
                  <td>{log.reason ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------

interface SubscriptionCardProps {
  subscription: UserDetailPayload['subscription'];
  active: boolean;
  plans: Plan[];
  busy: boolean;
  onStart: (planKey: string) => void;
  onCancel: () => void;
}

function SubscriptionCard({ subscription, active, plans, busy, onStart, onCancel }: SubscriptionCardProps) {
  const [planKey, setPlanKey] = useState(plans.find((plan) => !plan.isDefault)?.key ?? 'pro');

  return (
    <div className="panel">
      <h2>Subscription</h2>
      <dl className="row">
        <dt>Plan</dt>
        <dd>{planTag(subscription.plan)}</dd>
        <dt>Status</dt>
        <dd>{statusTag(subscription.status)}</dd>
        <dt>Provider</dt>
        <dd>{subscription.provider ?? <span className="muted">—</span>}</dd>
        <dt>Current period end</dt>
        <dd>{formatDate(subscription.currentPeriodEnd)}</dd>
        {subscription.gracePeriodEndsAt && (
          <>
            <dt>Grace period ends</dt>
            <dd>{formatDate(subscription.gracePeriodEndsAt)}</dd>
          </>
        )}
      </dl>
      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <select value={planKey} onChange={(event) => setPlanKey(event.target.value)}>
          {plans
            .filter((plan) => !plan.isDefault)
            .map((plan) => (
              <option key={plan.key} value={plan.key}>
                {plan.name} ({plan.key})
              </option>
            ))}
        </select>
        <button className="primary" disabled={busy} onClick={() => onStart(planKey)}>
          Start (mock)
        </button>
        {active && (
          <button className="danger" disabled={busy} onClick={onCancel}>
            Cancel now
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

interface EntitlementsCardProps {
  entitlements: UserDetailPayload['entitlements'];
  grants: UserDetailPayload['grants'];
  busy: boolean;
  onGrant: (payload: {
    featureKey: string;
    value: boolean | number | string;
    reason: string;
    expiresAt?: string;
  }) => void;
  onRevoke: (grantId: string) => void;
}

function EntitlementsCard({ entitlements, grants, busy, onGrant, onRevoke }: EntitlementsCardProps) {
  const [featureKey, setFeatureKey] = useState('');
  const [rawValue, setRawValue] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  return (
    <div className="panel">
      <h2>Entitlements — {entitlements.plan}</h2>
      <table>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(entitlements.features).map(([key, value]) => (
            <tr key={key}>
              <td>{key}</td>
              <td>
                <code>{String(value)}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {grants.length > 0 && (
        <>
          <h2 style={{ marginTop: 16 }}>Active grants</h2>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Value</th>
                <th>Source</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => (
                <tr key={grant.id}>
                  <td>{grant.featureKey}</td>
                  <td>
                    <code>{String(grant.value)}</code>
                  </td>
                  <td>{grant.source}</td>
                  <td>{formatDate(grant.validUntil)}</td>
                  <td>
                    <button className="link" disabled={busy} onClick={() => onRevoke(grant.id)}>
                      revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <form
        className="stack"
        style={{ marginTop: 16 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (!featureKey.trim() || !reason.trim() || !rawValue.trim()) return;
          onGrant({
            featureKey: featureKey.trim(),
            value: coerceValue(rawValue.trim()),
            reason: reason.trim(),
            expiresAt: expiresAt.trim() || undefined,
          });
          setFeatureKey('');
          setRawValue('');
          setReason('');
          setExpiresAt('');
        }}
      >
        <h2 style={{ margin: 0 }}>Add grant</h2>
        <div className="grid-two">
          <label>
            <span className="muted">Feature key</span>
            <input
              value={featureKey}
              onChange={(event) => setFeatureKey(event.target.value)}
              placeholder="e.g. cloud_storage_gb"
            />
          </label>
          <label>
            <span className="muted">Value</span>
            <input
              value={rawValue}
              onChange={(event) => setRawValue(event.target.value)}
              placeholder="true / 9999 / priority"
            />
          </label>
        </div>
        <label>
          <span className="muted">Reason</span>
          <input value={reason} onChange={(event) => setReason(event.target.value)} required />
        </label>
        <label>
          <span className="muted">Expires (ISO 8601, optional)</span>
          <input
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            placeholder="2027-01-01T00:00:00Z"
          />
        </label>
        <div>
          <button className="primary" type="submit" disabled={busy}>
            Grant
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Coerces the free-text value input into the right JSON type. Booleans and
 * numbers are what most features want; anything else stays as a string, which
 * matches how the API stores tiered features like `support_tier`.
 */
function coerceValue(raw: string): boolean | number | string {
  const lower = raw.toLowerCase();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  return raw;
}
